/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { TrackingConsent } from '@/types/tracking-consent';

/**
 * Custom event name published by the inline `OptanonWrapper()` callback in
 * `src/root.tsx`'s `<head>`. OneTrust invokes `window.OptanonWrapper()` after
 * every consent decision (Accept All, Reject All, Save Preferences), so
 * publishing a same-origin DOM event from that callback gives every subscriber
 * in the React tree a single hook to listen on without racing against the OT
 * script load order. Kept as a named export so the root inline script and the
 * bridge subscriber share the same string literal.
 */
export const ONETRUST_CONSENT_CHANGED_EVENT = 'ashley:onetrust:consent-changed';

/**
 * Map an OT active-groups set to the binary `TrackingConsent` value the SFCC
 * `dw_dnt` cookie encodes. Ashley's bridging rule (INT-60 §"OneTrust ⇄ dw_dnt
 * bridging"): `dw_dnt=0` (Accepted) when the shopper granted **either**
 * Performance (analytics) OR Targeting (advertising); `dw_dnt=1` (Declined)
 * otherwise. Necessary-only (C0001 with no others) counts as Declined because
 * SFCC's server-side Active Data + Einstein are the primary consumers of
 * `dw_dnt` and both are Performance-category tools.
 *
 * Returns `null` when the input is definitively "not yet determined" (e.g.
 * empty set from a still-loading OT script). Callers should treat `null` as
 * "wait — don't refresh SLAS yet" rather than "declined", so the SFN default
 * consent doesn't get baked in before the shopper has seen the banner.
 */
export function mapActiveGroupsToTrackingConsent(
    activeGroups: Set<string>,
    categoryMap: { analytics: string; advertising: string }
): TrackingConsent | null {
    if (activeGroups.size === 0) {
        return null;
    }
    const grantedAnalytics = activeGroups.has(categoryMap.analytics);
    const grantedAdvertising = activeGroups.has(categoryMap.advertising);
    return grantedAnalytics || grantedAdvertising ? TrackingConsent.Accepted : TrackingConsent.Declined;
}

/**
 * Read `window.OnetrustActiveGroups` and normalize it into a Set of group
 * IDs. OT emits this as a comma-separated string (e.g. `",C0001,C0002,"`)
 * with a leading and trailing comma — hence the `filter(Boolean)`. Returns
 * an empty Set when the string is missing or malformed rather than throwing;
 * the mapping helper treats "no active groups" as `Declined`.
 */
export function readActiveGroups(): Set<string> {
    if (typeof window === 'undefined') return new Set();
    const raw = window.OnetrustActiveGroups;
    if (typeof raw !== 'string' || raw.length === 0) return new Set();
    return new Set(raw.split(',').filter(Boolean));
}
